import React from 'react';
import { FileWithPath } from 'react-dropzone';
import { deleteFile, getFileByUrl, uploadFile } from '../../../sp-firebase/storageFB';

export type uploadErrorType = 'format' | 'size' | 'upload' | 'none' | 'multi';

export interface IUploadErrorObject {
  value: boolean;
  errorType: uploadErrorType;
}

export const DEFAULT_APPROVED_FILE_FORMATS = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

export const DEFAULT_MAX_FILE_SIZE = 20;

export const getFileName = (fullPath: string) => {
  const parts = fullPath.split('/');
  return parts[parts.length - 1];
};

export const handleRemoveFile = async (
  path: string,
  importedFilePaths: string[],
  setFileAttachmentStage: (value: React.SetStateAction<'empty' | 'uploadedFile' | 'pending'>) => void,
  setImportedFilePaths: (value: React.SetStateAction<string[]>) => void,
) => {
  await deleteFile(path);
  importedFilePaths.length === 1 && setFileAttachmentStage('empty');
  setImportedFilePaths(importedFilePaths.filter((item) => item != path));
};

export const handleRemoveAllFiles = async (
  importedFilePaths: string[],
  setImportedFilePaths: (value: string[]) => void,
  onDeleteAllFiles: () => void = () => {},
) => {
  importedFilePaths.forEach((path) => deleteFile(path));
  setImportedFilePaths([]);
  onDeleteAllFiles();
};

export const handleRemoveSingleFile = async (
  importedFilePath: string,
  setImportedFilePath: (value: string) => void,
  onDeleteAllFiles: () => void = () => {},
) => {
  deleteFile(importedFilePath);
  setImportedFilePath('');
  onDeleteAllFiles();
};

export const fetchDocumentsFromStorage = async (
  importedFilePaths: string[],
  setFilesUrl: (values: string[]) => void,
): Promise<string[]> => {
  const urls = await Promise.all(importedFilePaths.map((path) => getFileByUrl(path)));
  const filteredUrls = urls.filter((url) => typeof url === 'string') as string[];
  setFilesUrl(filteredUrls);
  return filteredUrls;
};

export const fetchDocumentFromStorage = async (
  importedFilePath: string,
  setFileUrl: (value: string) => void,
): Promise<string> => {
  try {
    const url = await getFileByUrl(importedFilePath);
    if (typeof url === 'string') {
      setFileUrl(url);
      return url;
    } else {
      throw new Error('Invalid URL format received');
    }
  } catch (error) {
    console.error('Error fetching document from storage:', error);
    throw error;
  }
};

export const uploadFilesToServer = async (
  acceptedFiles: File[],
  bucketSaveLocation: string,
  fileNameSuffix: string,
): Promise<string[]> => {
  const uploadedPaths = await Promise.all(
    acceptedFiles.map(async (file) => {
      const fileExtension = file.name.split('.').pop();
      const fileNameWithoutExtension = file.name.replace(`.${fileExtension}`, '');
      const newFileName = `${fileNameWithoutExtension}_${fileNameSuffix}.${fileExtension}`;

      const renamedFile = new File([file], newFileName, { type: file.type });
      return await uploadFile(renamedFile, bucketSaveLocation);
    }),
  );
  return uploadedPaths;
};

export const getDropzoneOrderConfig = (
  currentImportedFilePaths: string[],
  setCurrentImportedFilePaths: (_values: string[]) => void,
  setUploadError: (_uploadError: IUploadErrorObject) => void,
  bucketSaveLocation: string,
  fileNameSuffix: string = '',
  maxFileSize: number = DEFAULT_MAX_FILE_SIZE,
  approvedFileFormats: string[] = DEFAULT_APPROVED_FILE_FORMATS,
  uploadMulti: boolean = true,
  onLoadChange?: (_isLoading: boolean) => void,
) => ({
  disabled: currentImportedFilePaths.length > 0,
  onDrop: async (acceptedFiles: FileWithPath[]) => {
    const isInputValid = isValidUploadedFiles(acceptedFiles, maxFileSize, approvedFileFormats, uploadMulti);
    if (isInputValid !== null) {
      setUploadError(isInputValid);
      return;
    }
    onLoadChange?.(true);
    try {
      const uploadedFiles = await uploadFilesToServer(acceptedFiles, bucketSaveLocation, fileNameSuffix);

      setCurrentImportedFilePaths([...currentImportedFilePaths, ...uploadedFiles]);
      setUploadError({ errorType: 'none', value: false });
    } catch (error) {
      setUploadError({ errorType: 'upload', value: true });
      console.error('Error uploading files:', error);
    }
    onLoadChange?.(false);
  },
});

export const isValidUploadedFiles = (
  acceptedFiles: FileWithPath[],
  maxFileSize: number,
  approvedFileFormats: string[],
  isMulti: boolean,
): IUploadErrorObject | null => {
  console.log(acceptedFiles);

  if (!isMulti && acceptedFiles.length > 1) {
    return { errorType: 'multi', value: true };
  }

  if (acceptedFiles.some((file: FileWithPath) => file.size > maxFileSize * 1024 * 1024)) {
    return { errorType: 'size', value: true };
  }

  if (
    acceptedFiles.some((file: FileWithPath) => {
      return !approvedFileFormats.includes(file.type);
    })
  ) {
    return { errorType: 'format', value: true };
  }

  return null;
};

const fileExtensionRegex = /\.([a-zA-Z0-9]+)(?=\?|$)/;

export const getExtension = (fileName: string): string => {
  const matches = fileName.match(fileExtensionRegex);

  return matches?.[0] || '';
};
